import { isEqual, isNil, isNumber, isString, isUndefined } from 'lodash';
import { EditableField, Field } from './Field';

export interface TimeTrackerGeolocationCoordinates {
  lat: number;
  lon: number;
}

export interface TimeTrackerGeolocation {
  coords: TimeTrackerGeolocationCoordinates;
  accuracy?: number;
  userId: number;
}

export interface TimeTrackerEntry {
  timestamp: string;
  geolocation?: TimeTrackerGeolocation;
}

export interface TimeTrackerComponentDataValue {
  start: TimeTrackerEntry;
  end?: TimeTrackerEntry;
}

export type TimeTrackerComponentData = TimeTrackerComponentDataValue[];

export class TimeTrackerField implements EditableField {
  readonly timerEntries: TimeTrackerComponentData;
  readonly fieldType = 'TimeTrackerField';

  constructor(timerEntries: TimeTrackerComponentData) {
    this.timerEntries = timerEntries;
  }

  validate(): string | null {
    return null;
  }

  isEqual(field: Field): boolean {
    return field instanceof TimeTrackerField && isEqual(this.timerEntries, field.timerEntries);
  }

  toFieldJson() {
    return this.timerEntries;
  }

  get isEmpty() {
    return isNil(this.timerEntries);
  }

  static fromFieldJson(jsonBlob: any): TimeTrackerField | null {
    if (isTimeTrackerComponentData(jsonBlob)) {
      return new TimeTrackerField(jsonBlob);
    } else {
      return null;
    }
  }
}

function isGeolocationCoordinates(jsonBlob: any): jsonBlob is TimeTrackerGeolocationCoordinates {
  return !isNil(jsonBlob) && isNumber(jsonBlob.lat) && isNumber(jsonBlob.lon);
}

function isTimeTrackerGeolocation(jsonBlob: any): jsonBlob is TimeTrackerGeolocation {
  return (
    !isNil(jsonBlob) &&
    (isNumber(jsonBlob.accuracy) || isUndefined(jsonBlob.accuracy)) &&
    isGeolocationCoordinates(jsonBlob.coords) &&
    isNumber(jsonBlob.userId)
  );
}

function isTimeTrackerEntry(jsonBlob: any): jsonBlob is TimeTrackerEntry {
  return (
    !isNil(jsonBlob) &&
    (isTimeTrackerGeolocation(jsonBlob.geolocation) || isUndefined(jsonBlob.geolocation)) &&
    isString(jsonBlob.timestamp)
  );
}

export function isTimeTrackerDataItem(jsonBlob: any): jsonBlob is TimeTrackerComponentDataValue {
  return (
    !isNil(jsonBlob) &&
    isTimeTrackerEntry(jsonBlob.start) &&
    (isTimeTrackerEntry(jsonBlob.end) || isUndefined(jsonBlob.end))
  );
}

export function isTimeTrackerComponentData(jsonBlob: any): jsonBlob is TimeTrackerComponentData {
  return Array.isArray(jsonBlob) && jsonBlob.every(isTimeTrackerDataItem);
}
